// Chapter 1: Insurance Fundamentals for Crop
// Sections 5-6

export const ch1Section5 = {
  title: "Subrogation and Other Legal Doctrines",
  content: `
    <h2>Subrogation and Other Legal Doctrines</h2>
    <h3>Subrogation</h3>
    <p><strong>Subrogation</strong> is the insurer's right to "step into the shoes" of the insured and pursue recovery from a third party who caused the loss.</p>
    <div class="example">
      <h4>Subrogation Example:</h4>
      <p>A crop duster accidentally sprays wrong herbicide on a Spink County wheat field. The farmer's crop insurance pays the claim. The insurer then has subrogation rights to pursue the chemical company for reimbursement.</p>
    </div>
    <div class="important">
      <strong>Important:</strong> If an insured settles with a third party before the insurer can pursue subrogation, it may jeopardize the claim. Farmers should notify their insurer before settling with third parties.
    </div>
    <h3>Limits of Liability</h3>
    <p>In crop insurance, limits are expressed through:</p>
    <ul>
      <li><strong>Coverage Level:</strong> Percentage of expected yield/revenue (50%-85%)</li>
      <li><strong>Liability Cap:</strong> Maximum dollar amount per acre or policy</li>
      <li><strong>Unit Structure:</strong> How coverage is divided across farm operations</li>
    </ul>
    <h3>Negligence and Good Farming Practices</h3>
    <p>Farmers must follow <strong>good farming practices</strong>—production methods generally recognized for the area. Failure can void coverage.</p>
    <div class="key-concept">
      <strong>Good Farming Practices include:</strong>
      <p>Proper planting dates, appropriate seed selection, adequate pest management, and normal cultivation practices.</p>
    </div>
    <h3>Key Insurance Terms</h3>
    <table>
      <tr><th>Term</th><th>Definition</th></tr>
      <tr><td><strong>Premium</strong></td><td>Price paid for coverage</td></tr>
      <tr><td><strong>Deductible</strong></td><td>Amount insured pays before insurance pays (built into coverage level)</td></tr>
      <tr><td><strong>Indemnity</strong></td><td>Payment made when covered loss occurs</td></tr>
      <tr><td><strong>Policy Period</strong></td><td>Time during which coverage is in effect</td></tr>
      <tr><td><strong>Endorsement</strong></td><td>Modification to standard policy terms</td></tr>
    </table>
  `
};

export const ch1Section6 = {
  title: "Crop Insurance Organizations",
  content: `
    <h2>Crop Insurance Organizations</h2>
    <h3>Federal Crop Insurance Corporation (FCIC)</h3>
    <p>A government corporation within USDA created in 1938. FCIC is the legal entity providing crop insurance but operates through AIPs.</p>
    <div class="key-concept">
      <strong>FCIC Responsibilities:</strong>
      <ul>
        <li>Establishes policies and provisions</li>
        <li>Sets premium rates and subsidy levels</li>
        <li>Provides reinsurance to AIPs</li>
        <li>Approves new insurance products</li>
      </ul>
    </div>
    <h3>USDA Risk Management Agency (RMA)</h3>
    <p>Administers crop insurance programs on behalf of FCIC:</p>
    <ul>
      <li>Develops actuarial documents</li>
      <li>Establishes important dates (sales closing, acreage reporting)</li>
      <li>Oversees compliance and quality control</li>
    </ul>
    <h3>Approved Insurance Providers (AIPs)</h3>
    <p>Private companies approved to sell federal crop insurance:</p>
    <ul>
      <li>Sell policies to farmers</li>
      <li>Collect premiums (largely subsidized)</li>
      <li>Process and pay claims</li>
      <li>Share risk through Standard Reinsurance Agreement (SRA)</li>
    </ul>
    <h3>National Crop Insurance Services (NCIS)</h3>
    <p>Provides services for <strong>private crop hail insurance</strong> (NOT federal MPCI):</p>
    <ul>
      <li>Develops standard crop hail policy forms</li>
      <li>Provides statistical/actuarial services</li>
      <li>Maintains loss adjustment standards</li>
    </ul>
    <div class="important">
      <strong>Important:</strong> NCIS is for <strong>crop hail insurance</strong>—private, NOT federally subsidized. Different from MPCI administered by RMA/FCIC.
    </div>
    <h3>Federal Crop Insurance Act of 1980</h3>
    <p>Landmark legislation creating modern crop insurance:</p>
    <ul>
      <li>Expanded to additional crops and regions</li>
      <li>Introduced premium subsidies</li>
      <li>Created public-private delivery system using AIPs</li>
    </ul>
    <div class="exam-tip">
      <strong>Exam Tip:</strong> The 1980 Act created modern crop insurance. Remember: 1980 = premium subsidies + private AIPs.
    </div>
    <h3>South Dakota's Role</h3>
    <p>The SD Division of Insurance regulates crop insurance agents:</p>
    <ul>
      <li>Licenses crop insurance producers</li>
      <li>Enforces ethical standards</li>
      <li>Handles complaints against agents</li>
      <li>Can suspend or revoke licenses</li>
    </ul>
  `
};
